/**
 * LHS Command Centre - Operational Control Cockpit
 * 
 * Features:
 * - Live health score with traffic light
 * - Active alerts (anomaly engine)
 * - Jobs table with filters, search, one-click actions
 * - Invoices table with validation/override
 * - Autofix and cleanup controls
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { downloadSystemBackup } from '../lib/backup';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ---------- API CLIENT ----------
const api = axios.create({ baseURL: `${API_URL}/api` });
api.interceptors.request.use((config) => {
  // Try lhs_token first, then fall back to regular token
  const token = localStorage.getItem('lhs_token') || localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------- UI COMPONENTS ----------
const Pill = ({ children, color = '#1f2937' }) => (
  <span style={{
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: color,
    marginRight: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }}>{children}</span>
);

const Button = ({ children, onClick, variant = 'primary', disabled, size = 'md' }) => {
  const styles = {
    primary: { background: '#2563eb', color: '#fff', border: 'none' },
    success: { background: '#16a34a', color: '#fff', border: 'none' },
    danger: { background: '#dc2626', color: '#fff', border: 'none' },
    warning: { background: '#d97706', color: '#fff', border: 'none' },
    ghost: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' }
  }[variant];
  
  const sizes = {
    sm: { padding: '4px 8px', fontSize: 11 },
    md: { padding: '8px 14px', fontSize: 13 },
    lg: { padding: '10px 18px', fontSize: 14 }
  }[size];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...styles,
        ...sizes,
        borderRadius: 8,
        marginRight: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontWeight: 500,
        transition: 'all 0.15s ease'
      }}
    >
      {children}
    </button>
  );
};

const Card = ({ title, right, children, highlight }) => (
  <div style={{ 
    background: '#fff', 
    borderRadius: 12, 
    padding: 16, 
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: highlight ? `2px solid ${highlight}` : '1px solid #e5e7eb',
    height: '100%'
  }}>
    {(title || right) && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {title && <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#374151' }}>{title}</h3>}
        {right}
      </div>
    )}
    {children}
  </div>
);

const Section = ({ title, right, children }) => (
  <div style={{ marginBottom: 20 }}>
    {(title || right) && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {title && <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>{title}</h3>}
        {right}
      </div>
    )}
    <div style={{ 
      background: '#fff', 
      borderRadius: 12, 
      padding: 16, 
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      {children}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    'NEW': '#6366f1',
    'AWAITING_QUOTE': '#8b5cf6',
    'AWAITING_CLIENT_APPROVAL': '#d97706',
    'APPROVED': '#16a34a',
    'ASSIGNED': '#0891b2',
    'SCHEDULED': '#0284c7',
    'IN_PROGRESS': '#2563eb',
    'COMPLETED': '#16a34a',
    'READY_TO_INVOICE': '#7c3aed',
    'INVOICED': '#059669',
    'CLOSED': '#6b7280',
    'ON_HOLD': '#f59e0b',
    'CANCELLED': '#ef4444',
    'REJECTED': '#dc2626',
    // Legacy statuses
    'Job Created': '#6366f1',
    'Awaiting Quote': '#8b5cf6',
    'Quote Submitted': '#d97706',
    'Assigned': '#0891b2',
    'Scheduled': '#0284c7',
    'In Progress': '#2563eb',
    'Completed': '#16a34a',
  };
  return <Pill color={colors[status] || '#6b7280'}>{status}</Pill>;
};

const SLABadge = ({ sla }) => {
  const colors = {
    'OVERDUE': '#dc2626',
    'DUE_SOON': '#d97706',
    'ON_TRACK': '#16a34a',
    'NO_DUE_DATE': '#6b7280',
    'CLOSED': '#9ca3af'
  };
  return <Pill color={colors[sla] || '#6b7280'}>{sla || 'N/A'}</Pill>;
};

// ---------- MAIN COMPONENT ----------
export default function LHSCommandCentre() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState({ status: 'ALL', sla: 'ALL' });
  const [busy, setBusy] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);

  // ---- BACKUP ----
  const handleBackup = async () => {
    const success = await downloadSystemBackup(setBackupLoading);
    if (success) {
      // Optionally reload to update backup status
    }
  };

  // ---- DATA FETCHING ----
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, alertsRes, jobsRes, invoicesRes, contractorsRes] = await Promise.all([
        api.get('/lhs/health'),
        api.get('/lhs/alerts'),
        api.get('/lhs/jobs'),
        api.get('/lhs/invoices'),
        api.get('/contractors').catch(() => ({ data: [] }))
      ]);
      
      setHealth(healthRes.data.data || healthRes.data);
      setAlerts(alertsRes.data.alerts || alertsRes.data.data || []);
      setJobs(jobsRes.data.jobs || jobsRes.data.data || []);
      setInvoices(invoicesRes.data.invoices || invoicesRes.data.data || []);
      setContractors(contractorsRes.data.contractors || contractorsRes.data || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Load error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---- ACTIONS ----
  const assignContractor = async (job_id, job_ref) => {
    const contractorOptions = contractors.map(c => 
      `${c.id}: ${c.company_name || c.name}`
    ).join('\n');
    
    const contractor_id = prompt(
      `Assign contractor to ${job_ref}\n\nAvailable:\n${contractorOptions || 'No contractors found'}\n\nEnter contractor ID:`
    );
    if (!contractor_id) return;
    
    setBusy(true);
    try {
      await api.post(`/lhs/jobs/${job_id}/assign`, { contractor_id: contractor_id.trim() });
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  const overrideStatus = async (job_id, job_ref, currentStatus) => {
    const statuses = [
      'AWAITING_QUOTE', 'AWAITING_CLIENT_APPROVAL', 'APPROVED', 'ASSIGNED',
      'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'READY_TO_INVOICE', 'INVOICED',
      'CLOSED', 'ON_HOLD', 'CANCELLED'
    ];
    
    const status = prompt(
      `Override status for ${job_ref}\nCurrent: ${currentStatus}\n\nOptions:\n${statuses.join(', ')}\n\nEnter new status:`
    );
    if (!status) return;
    
    const reason = prompt('Reason for override:') || 'Manual override from Command Centre';
    
    setBusy(true);
    try {
      await api.post(`/lhs/jobs/${job_id}/override-status`, { 
        new_status: status.trim(), 
        reason,
        force: true 
      });
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  const forceComplete = async (job_id, job_ref) => {
    if (!window.confirm(`Force complete job ${job_ref}?\n\nThis will bypass normal completion checks.`)) return;
    
    const reason = prompt('Reason for force completion:') || 'Ops intervention from Command Centre';
    
    setBusy(true);
    try {
      await api.post(`/lhs/jobs/${job_id}/force-complete`, { 
        reason,
        bypass_checks: true 
      });
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  const validateInvoice = async (invoice_id) => {
    setBusy(true);
    try {
      await api.post(`/lhs/invoices/${invoice_id}/validate`, {
        action: 'approve',
        reason: 'Validated from Command Centre'
      });
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  const overrideInvoice = async (invoice_id) => {
    if (!window.confirm('Override invoice to APPROVED_FOR_PAYMENT?\n\nThis will bypass validation checks.')) return;
    
    const reason = prompt('Reason for override:') || 'Ops override from Command Centre';
    
    setBusy(true);
    try {
      await api.post(`/lhs/invoices/${invoice_id}/override`, { reason });
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  const runAutofix = async () => {
    if (!window.confirm('Run autofix?\n\nThis will:\n- Supersede duplicate quotes\n- Refresh completion flags')) return;
    
    setBusy(true);
    try {
      const res = await api.post('/lhs/autofix');
      const fixes = res.data.data?.fixes || [];
      alert(`Autofix complete!\n\nFixes applied: ${fixes.length}`);
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  const runCleanup = async () => {
    if (!window.confirm('Cleanup orphan data?\n\nThis will permanently delete:\n- Orphan invoices\n- Orphan quotes\n- Jobs with invalid clients\n\nThis action cannot be undone.')) return;
    
    setBusy(true);
    try {
      const res = await api.post('/lhs/cleanup-orphans');
      const removed = res.data.data?.removed || {};
      alert(`Cleanup complete!\n\nRemoved:\n- Invoices: ${removed.invoices || 0}\n- Quotes: ${removed.quotes || 0}\n- Jobs: ${removed.jobs || 0}\n- Fixed jobs: ${removed.fixed_jobs || 0}`);
      await loadAll();
    } catch (err) {
      alert(`Error: ${err.response?.data?.detail || err.message}`);
    } finally { 
      setBusy(false); 
    }
  };

  // ---- DERIVED DATA ----
  const filteredJobs = useMemo(() => {
    return jobs
      .filter(j => filter.status === 'ALL' || j.status === filter.status)
      .filter(j => filter.sla === 'ALL' || j.sla_status === filter.sla)
      .filter(j => {
        if (!query) return true;
        const q = query.toLowerCase();
        return [j.id, j.job_reference, j.title, j.site_name, j.park_name, j.asset_unit]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(q));
      });
  }, [jobs, filter, query]);

  const alertSummary = useMemo(() => {
    const summary = { critical: 0, high: 0, medium: 0, low: 0 };
    alerts.forEach(a => {
      const level = (a.severity || a.level || '').toLowerCase();
      if (summary.hasOwnProperty(level)) summary[level]++;
    });
    return summary;
  }, [alerts]);

  const healthScore = health?.health?.score ?? health?.score ?? 0;
  const healthTraffic = health?.health?.traffic ?? health?.traffic ?? 'UNKNOWN';
  const healthTotals = health?.health?.totals ?? health?.totals ?? {};

  const trafficColor = {
    'GREEN': '#16a34a',
    'AMBER': '#d97706',
    'RED': '#dc2626'
  }[healthTraffic] || '#6b7280';

  // ---- RENDER ----
  if (loading && !health) {
    return (
      <div style={{ 
        padding: 40, 
        textAlign: 'center', 
        background: '#f8fafc', 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>Loading Command Centre...</div>
        <div style={{ color: '#6b7280' }}>Fetching system data</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, background: '#fef2f2', minHeight: '100vh' }}>
        <h2 style={{ color: '#dc2626' }}>Error Loading Command Centre</h2>
        <p>{error}</p>
        <Button onClick={loadAll}>Retry</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, background: '#f8fafc', minHeight: '100vh' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111827' }}>
            LHS Command Centre
          </h1>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Last refresh: {lastRefresh?.toLocaleTimeString() || 'Never'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleBackup}
            disabled={backupLoading}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: backupLoading ? '#9ca3af' : '#16a34a',
              color: '#fff',
              fontWeight: 600,
              cursor: backupLoading ? 'wait' : 'pointer',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            {backupLoading ? 'Downloading...' : '⬇ Download Backup'}
          </button>
          <Button onClick={loadAll} disabled={busy || loading} size="lg">
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* TOP METRICS ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        {/* Health Score */}
        <Card title="System Health" highlight={trafficColor}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 36, fontWeight: 700, color: trafficColor }}>{healthScore}</span>
            <Pill color={trafficColor}>{healthTraffic}</Pill>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Jobs: {healthTotals.jobs || 0} • Quotes: {healthTotals.quotes || 0} • Invoices: {healthTotals.invoices || 0}
          </div>
        </Card>

        {/* Alerts Summary */}
        <Card title="Active Alerts">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Pill color="#dc2626">Critical: {alertSummary.critical}</Pill>
            <Pill color="#d97706">High: {alertSummary.high}</Pill>
            <Pill color="#6b7280">Medium: {alertSummary.medium}</Pill>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Total: {alerts.length} alerts requiring attention
          </div>
        </Card>

        {/* Integrity */}
        <Card title="Data Integrity">
          <div style={{ fontSize: 24, fontWeight: 600 }}>
            {healthTotals.integrity_issues || 0} issues
          </div>
          <div style={{ marginTop: 8 }}>
            <Button onClick={runAutofix} disabled={busy} size="sm">AutoFix</Button>
            <Button onClick={runCleanup} variant="danger" disabled={busy} size="sm">Cleanup</Button>
          </div>
        </Card>

        {/* Anomalies */}
        <Card title="Anomalies">
          <div style={{ fontSize: 24, fontWeight: 600 }}>
            {healthTotals.anomalies || 0} detected
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Activities logged: {healthTotals.activities || 0}
          </div>
        </Card>
      </div>

      {/* ALERTS LIST */}
      {alerts.length > 0 && (
        <Section title={`Active Alerts (${alerts.length})`}>
          <div style={{ maxHeight: 250, overflowY: 'auto' }}>
            {alerts.map((a, idx) => (
              <div key={idx} style={{ 
                padding: '10px 12px', 
                borderBottom: idx < alerts.length - 1 ? '1px solid #f1f5f9' : 'none',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12
              }}>
                <Pill color={
                  (a.severity || a.level) === 'critical' ? '#dc2626' : 
                  (a.severity || a.level) === 'high' ? '#d97706' : '#6b7280'
                }>
                  {a.severity || a.level}
                </Pill>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: '#111827' }}>{a.type || a.code}</strong>
                  <span style={{ color: '#6b7280', marginLeft: 8 }}>{a.label || a.message}</span>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                    {a.entity_reference || a.context?.title || a.job_id}
                    {a.next_action && <span> → {a.next_action}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* JOBS TABLE */}
      <Section 
        title={`Jobs (${filteredJobs.length}/${jobs.length})`}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              placeholder="Search jobs..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ 
                padding: '8px 12px', 
                borderRadius: 8, 
                border: '1px solid #d1d5db',
                width: 200,
                fontSize: 13
              }}
            />
            <select
              value={filter.status}
              onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
            >
              <option value="ALL">All Status</option>
              {['AWAITING_QUOTE', 'AWAITING_CLIENT_APPROVAL', 'APPROVED', 'ASSIGNED', 'SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'READY_TO_INVOICE', 'INVOICED', 'CLOSED', 'ON_HOLD', 'CANCELLED']
                .map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={filter.sla}
              onChange={e => setFilter(f => ({ ...f, sla: e.target.value }))}
              style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 }}
            >
              <option value="ALL">All SLA</option>
              <option value="OVERDUE">Overdue</option>
              <option value="DUE_SOON">Due Soon</option>
              <option value="ON_TRACK">On Track</option>
            </select>
          </div>
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Job</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Site / Unit</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>SLA</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Contractor</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Next Action</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                    No jobs match filters
                  </td>
                </tr>
              ) : (
                filteredJobs.map(j => (
                  <tr key={j.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{j.job_reference || j.id?.slice(0, 12)}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{j.title?.slice(0, 30)}</div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div>{j.park_name || j.site_name || '-'}</div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{j.asset_unit || '-'}</div>
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <StatusBadge status={j.status} />
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <SLABadge sla={j.sla_status} />
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: 12 }}>
                      {j.contractor?.company_name || j.assigned_contractor_name || j.contractor_name || '-'}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: 12, maxWidth: 200, color: '#6b7280' }}>
                      {j.next_action || '-'}
                    </td>
                    <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                      <Button 
                        onClick={() => assignContractor(j.id, j.job_reference)} 
                        disabled={busy} 
                        size="sm"
                      >
                        Assign
                      </Button>
                      <Button 
                        onClick={() => overrideStatus(j.id, j.job_reference, j.status)} 
                        variant="ghost" 
                        disabled={busy}
                        size="sm"
                      >
                        Override
                      </Button>
                      <Button 
                        onClick={() => forceComplete(j.id, j.job_reference)} 
                        variant="warning" 
                        disabled={busy}
                        size="sm"
                      >
                        Complete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* INVOICES TABLE */}
      <Section title={`Invoices (${invoices.length})`}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Invoice</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Job</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Approved</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Invoice</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Variance</th>
                <th style={{ padding: '10px 8px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>
                    No invoices
                  </td>
                </tr>
              ) : (
                invoices.map(inv => {
                  const invId = inv.id || inv.invoice_id;
                  const variance = inv.variance ?? (inv.invoice_amount - (inv.approved_amount || 0));
                  const hasVariance = inv.has_variance || variance > 0;
                  
                  return (
                    <tr key={invId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ fontWeight: 500 }}>{invId?.slice(0, 16) || '-'}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{inv.source || 'contractor'}</div>
                      </td>
                      <td style={{ padding: '12px 8px', fontSize: 12 }}>
                        {inv.job?.job_reference || inv.job_id?.slice(0, 15) || '-'}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <StatusBadge status={inv.status} />
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        £{(inv.approved_amount || inv.approved_value || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        £{(inv.invoice_amount || inv.total_amount || inv.amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <Pill color={hasVariance ? '#dc2626' : '#16a34a'}>
                          {variance > 0 ? '+' : ''}£{variance?.toFixed(2) || '0.00'}
                          {inv.variance_percent ? ` (${inv.variance_percent}%)` : ''}
                        </Pill>
                      </td>
                      <td style={{ padding: '12px 8px', whiteSpace: 'nowrap' }}>
                        <Button 
                          onClick={() => validateInvoice(invId)} 
                          disabled={busy}
                          size="sm"
                          variant="success"
                        >
                          Validate
                        </Button>
                        <Button 
                          onClick={() => overrideInvoice(invId)} 
                          variant="danger" 
                          disabled={busy}
                          size="sm"
                        >
                          Override
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
